import { IpaType } from "../phonemes/ipa";

/**
 * How particular phonemes might change in different circumstances.
 * Interpretation: a:[['b','c'],['d','e']] should be read as
 * "a might turn into b or c after one step, or d or e after two steps"
 */
export interface ReplacementOptions {
  [phoneme: IpaType]: (IpaType | IpaType[])[];
}

/**
 * for each phoneme in 'ipa', replace it with the closest possibility in 'options'
 * if the resulting phoneme is in checkConsonants,
 * return the phoneme + the number of steps in the shortest path found in 'options'.
 */
export function tryOptionalReplacements(
  ipa: IpaType, checkConsonants: IpaType[], options: ReplacementOptions[]
): { match: IpaType, steps: number } | null {
  const within = new Set<IpaType>(checkConsonants);
  let match = '';
  let steps = Number.MAX_VALUE;
  options.forEach(subset => {
    const results = checkSubset(ipa, within, subset);
    if (results !== null && results.steps < steps) {
      match = results.match;
      steps = results.steps;
    }
  });
  return steps === Number.MAX_VALUE ? null : { match, steps };
}

function checkSubset(
  ipa: IpaType, within: Set<IpaType>, check: ReplacementOptions,
): { match: IpaType, steps: number } | null {
  const possible = check[ipa];
  if (possible === undefined) {
    return null;
  }

  let steps = 0;
  for (const chunk of possible) {
    if (typeof chunk === 'string') {
      if (within.has(chunk)) {
        return { match: chunk, steps };
      }
    } else {
      for (const s of chunk) {
        if (within.has(s)) {
          return { match: s, steps };
        }
      }
    }
    steps++;
  }
  return null;
}
